import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, ArrowLeft, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickModeStepProps {
  initialValue: string;
  onNext: (value: string) => void;
  onBack: () => void;
}

export const QuickModeStep = ({ initialValue, onNext, onBack }: QuickModeStepProps) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && value.trim()) {
        onNext(value);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [value, onNext]);

  const isValid = value.trim().length >= 20;
  const progress = Math.min(100, (value.trim().length / 20) * 100);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Zap className="h-5 w-5" />
          </div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quick Mode</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-foreground">
          Erzähl mir von deiner Situation
        </h2>
        <p className="text-muted-foreground text-base">
          In eigenen Worten – ich frage nach, was ich brauche
        </p>
      </div>

      <div className="space-y-3">
        <div className={cn(
          "relative rounded-xl border bg-card/50 backdrop-blur-sm transition-all duration-300 overflow-hidden",
          isFocused ? "border-primary shadow-[0_0_20px_hsl(var(--primary)/0.1)]" : "border-border/60"
        )}>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Was ist passiert? Was steht auf dem Spiel? Und wie dringend ist es?"
            className="min-h-[200px] text-base resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
          {/* Progress bar */}
          <div className="h-1 bg-muted/30">
            <div 
              className={cn(
                "h-full transition-all duration-300",
                isValid ? "bg-green-500" : "bg-primary/60"
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{value.trim().length} / 20+ Zeichen</span>
          <span className="text-xs opacity-60">⌘ + Enter zum Fortfahren</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border/60 hover:bg-card hover:border-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={() => onNext(value)}
          disabled={!isValid}
          className="flex-1 gap-2 h-12 rounded-xl"
        >
          Weiter
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
